import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { fetchAuthSession } from '../util/amplifyAuth';
import { useUserFiles } from '../context/UserFilesProvider';
import { findProtocolFile } from '../util/ProtocolUtil';
import {
    getTodayKey,
    loadComplianceData,
    saveComplianceData,
    calculateStreak,
    calculateComplianceRate,
    getHistoryDays,
    getChecklistForDate,
    getWeeklyChecklistForDate,
    getWeeklyProgress,
    mergeComplianceData,
} from './complianceUtils';

const UPLOAD_ENDPOINT = `${import.meta.env.VITE_API_BASE_URL}/upload`;
const SYNC_INTERVAL_MS = 60_000;

/**
 * Upload compliance to its era-dated file (compliance_<YYYYMMDD>.json, paired
 * 1:1 with protocol_<YYYYMMDD>.json). eraDate comes from the protocol FILE's
 * date — null means a legacy plain protocol.json, which pairs with plain
 * compliance.json. Days before the era start are trimmed so each era's file
 * contains only its own data (no unbounded growth, no cross-era leakage).
 */
async function uploadCompliance(data, patientId, eraDate = null) {
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString();
    if (!token) return;
    const userId = session.tokens?.idToken?.payload?.sub;

    let days = data;
    if (eraDate) {
        const eraStart = `${eraDate.slice(0, 4)}-${eraDate.slice(4, 6)}-${eraDate.slice(6, 8)}`;
        days = Object.fromEntries(
            Object.entries(data).filter(([dateKey]) => dateKey >= eraStart)
        );
    }

    const envelope = {
        type: 'compliance',
        version: 1,
        lastSyncedAt: new Date().toISOString(),
        days,
    };

    const filename = eraDate ? `compliance_${eraDate}.json` : 'compliance.json';
    const blob = new Blob([JSON.stringify(envelope)], { type: 'application/json' });
    const file = new File([blob], filename, { type: 'application/json' });
    const formData = new FormData();
    formData.append('file', file);
    formData.append('filename', filename);

    const headers = { authorization: `Bearer ${token}` };
    headers['x-patient-id'] = patientId || userId;

    await fetch(UPLOAD_ENDPOINT, { method: 'POST', headers, body: formData });
}

export function useComplianceState(supplements, { serverData = null, patientId = null, userId = null, weeklySupplements = [], eraDate = null } = {}) {
    const isDoctorView = !!patientId;

    const [data, setData] = useState(() => {
        if (isDoctorView) return serverData?.days || {};
        const local = loadComplianceData(userId);
        const serverDays = serverData?.days;
        return serverDays ? mergeComplianceData(local, serverDays) : local;
    });
    const [todayKey, setTodayKey] = useState(getTodayKey);
    const [selectedDate, setSelectedDate] = useState(getTodayKey);

    // Detect date change on visibility (handles midnight rollover)
    useEffect(() => {
        const checkDate = () => {
            if (document.visibilityState !== 'visible') return;
            const current = getTodayKey();
            setTodayKey(prev => {
                if (prev !== current) {
                    setSelectedDate(current);
                    return current;
                }
                return prev;
            });
        };
        document.addEventListener('visibilitychange', checkDate);
        return () => document.removeEventListener('visibilitychange', checkDate);
    }, []);

    // Incorporate server data when it arrives asynchronously (e.g., after file fetch)
    useEffect(() => {
        if (!serverData?.days) return;
        setData(isDoctorView ? serverData.days : prev => mergeComplianceData(prev, serverData.days));
    }, [serverData, isDoctorView]);

    // Era trim: drop local days from before the current protocol era so streak/rate
    // and localStorage stay scoped to the era (matches the upload-time trim — a new
    // era starts with a clean checklist, and local storage doesn't grow unboundedly).
    useEffect(() => {
        if (!eraDate || isDoctorView) return;
        const eraStart = `${eraDate.slice(0, 4)}-${eraDate.slice(4, 6)}-${eraDate.slice(6, 8)}`;
        setData(prev => {
            const stale = Object.keys(prev).filter(k => k < eraStart);
            if (!stale.length) return prev;
            const next = { ...prev };
            stale.forEach(k => delete next[k]);
            return next;
        });
    }, [eraDate, isDoctorView]);

    const dirtyRef = useRef(false);
    const dataRef = useRef(data);
    const patientIdRef = useRef(patientId);
    const eraDateRef = useRef(eraDate);
    dataRef.current = data;
    patientIdRef.current = patientId;
    eraDateRef.current = eraDate;

    // Mark dirty on every data change
    const setDataDirty = useCallback((updater) => {
        setData(prev => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            dirtyRef.current = true;
            return next;
        });
    }, []);

    // Sync to localStorage on every change (skip for doctor view)
    useEffect(() => {
        if (!isDoctorView) saveComplianceData(userId, data);
    }, [data, isDoctorView, userId]);

    // Flush to server if dirty — only clear dirty flag on success
    const flushSync = useCallback(() => {
        if (!dirtyRef.current) return;
        const snapshot = dataRef.current;
        uploadCompliance(snapshot, patientIdRef.current, eraDateRef.current)
            .then(() => {
                // Only clear if data hasn't changed since we started the upload
                if (dataRef.current === snapshot) dirtyRef.current = false;
            })
            .catch(err => console.error('Compliance sync failed:', err));
    }, []);

    // Batched upload: visibilitychange + interval + beforeunload (skip for doctor view)
    useEffect(() => {
        if (isDoctorView) return;
        const handleVisibility = () => {
            if (document.visibilityState === 'hidden') flushSync();
        };
        const handleUnload = () => flushSync();

        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('beforeunload', handleUnload);
        const interval = setInterval(flushSync, SYNC_INTERVAL_MS);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('beforeunload', handleUnload);
            clearInterval(interval);
            flushSync();
        };
    }, [flushSync, isDoctorView]);

    // Use supplement name as unique key
    const itemKeys = useMemo(
        () => (supplements || []).map(s => s.supplement),
        [supplements]
    );

    // Weekly item keys and targets
    const weeklyItemKeys = useMemo(
        () => (weeklySupplements || []).map(s => s.supplement),
        [weeklySupplements]
    );
    const weeklyTargets = useMemo(
        () => {
            const targets = {};
            for (const s of (weeklySupplements || [])) {
                targets[s.supplement] = s.frequency || 1;
            }
            return targets;
        },
        [weeklySupplements]
    );

    // Today's checklist (always reflects today, used for progress bar)
    const todayChecklist = useMemo(
        () => getChecklistForDate(data, todayKey, itemKeys),
        [data, todayKey, itemKeys]
    );

    // Selected date's checklist
    const selectedChecklist = useMemo(
        () => getChecklistForDate(data, selectedDate, itemKeys),
        [data, selectedDate, itemKeys]
    );

    // Weekly checklists (what's checked today for weekly items)
    const selectedWeeklyChecklist = useMemo(
        () => getWeeklyChecklistForDate(data, selectedDate, weeklyItemKeys),
        [data, selectedDate, weeklyItemKeys]
    );

    // Weekly progress (aggregated across the week)
    const weeklyProgress = useMemo(
        () => getWeeklyProgress(data, selectedDate, weeklyItemKeys, weeklyTargets),
        [data, selectedDate, weeklyItemKeys, weeklyTargets]
    );

    const isComplete = useMemo(
        () => itemKeys.length > 0 && itemKeys.every(c => todayChecklist[c]),
        [itemKeys, todayChecklist]
    );

    const isSelectedComplete = useMemo(
        () => itemKeys.length > 0 && itemKeys.every(c => selectedChecklist[c]),
        [itemKeys, selectedChecklist]
    );

    const toggleItem = useCallback((key) => {
        setDataDirty(prev => {
            const dayData = prev[selectedDate] || { completedAt: null, items: {} };
            const newItems = { ...dayData.items, [key]: !dayData.items[key] };
            const allChecked = itemKeys.every(c => newItems[c] === true);
            return {
                ...prev,
                [selectedDate]: {
                    ...dayData,
                    items: newItems,
                    completedAt: allChecked ? new Date().toISOString() : null,
                },
            };
        });
    }, [selectedDate, itemKeys, setDataDirty]);

    const toggleWeeklyItem = useCallback((key) => {
        setDataDirty(prev => {
            const dayData = prev[selectedDate] || { completedAt: null, items: {}, weeklyItems: {} };
            const weeklyItems = { ...(dayData.weeklyItems || {}), [key]: !dayData.weeklyItems?.[key] };
            return {
                ...prev,
                [selectedDate]: {
                    ...dayData,
                    weeklyItems,
                },
            };
        });
    }, [selectedDate, setDataDirty]);

    const checkAll = useCallback(() => {
        setDataDirty(prev => {
            const dayData = prev[selectedDate] || {};
            const items = {};
            for (const k of itemKeys) items[k] = true;
            const weeklyItems = { ...(dayData.weeklyItems || {}) };
            for (const k of weeklyItemKeys) weeklyItems[k] = true;
            return {
                ...prev,
                [selectedDate]: {
                    ...dayData,
                    items,
                    weeklyItems,
                    completedAt: new Date().toISOString(),
                },
            };
        });
    }, [selectedDate, itemKeys, weeklyItemKeys, setDataDirty]);

    // --- Notes ---
    const selectedNote = useMemo(
        () => data[selectedDate]?.note || '',
        [data, selectedDate]
    );

    const setSelectedNote = useCallback((text) => {
        setDataDirty(prev => {
            const dayData = prev[selectedDate] || { completedAt: null, items: {} };
            const updated = { ...dayData };
            if (text) {
                updated.note = text;
            } else {
                delete updated.note;
            }
            return { ...prev, [selectedDate]: updated };
        });
    }, [selectedDate, setDataDirty]);

    const allNotes = useMemo(() => {
        return Object.entries(data)
            .filter(([, day]) => day.note)
            .map(([date, day]) => ({ date, note: day.note }))
            .sort((a, b) => b.date.localeCompare(a.date));
    }, [data]);

    const streak = useMemo(() => calculateStreak(data), [data]);
    const complianceRate = useMemo(() => calculateComplianceRate(data), [data]);
    const history = useMemo(() => getHistoryDays(data, 7), [data]);

    const todayCheckedCount = useMemo(
        () => Object.values(todayChecklist).filter(Boolean).length,
        [todayChecklist]
    );

    // Aggregate weekly progress: total done / total target across all weekly items
    const weeklyCheckedCount = useMemo(
        () => Object.values(weeklyProgress).reduce((sum, p) => sum + p.done, 0),
        [weeklyProgress]
    );
    const weeklyTotalCount = useMemo(
        () => Object.values(weeklyProgress).reduce((sum, p) => sum + p.target, 0),
        [weeklyProgress]
    );

    return {
        todayChecklist,
        selectedChecklist,
        selectedWeeklyChecklist,
        weeklyProgress,
        selectedDate,
        setSelectedDate,
        toggleItem,
        toggleWeeklyItem,
        checkAll,
        isComplete,
        isSelectedComplete,
        streak,
        history,
        complianceRate,
        todayCheckedCount,
        totalCount: itemKeys.length,
        weeklyCheckedCount,
        weeklyTotalCount,
        selectedNote,
        setSelectedNote,
        allNotes,
        flushSync,
    };
}

/**
 * Combined hook: extracts server compliance from userFiles and initializes compliance state.
 * Use this instead of manually extracting the compliance file + calling useComplianceState.
 *
 * Era pairing: compliance follows the protocol FILE's date — protocol_<era>.json
 * pairs with compliance_<era>.json; legacy plain protocol.json pairs with plain
 * compliance.json. A compliance file from a different era is ignored (a freshly
 * regenerated protocol starts with a clean checklist).
 */
export function useCompliance(supplements, { patientId = null, userId = null, weeklySupplements = [] } = {}) {
    const { userFiles } = useUserFiles();

    const { serverData, eraDate } = useMemo(() => {
        const protocolFile = findProtocolFile(userFiles);
        const dated = protocolFile?.key.match(/^protocol_(\d{8})\.json$/);
        const era = dated ? dated[1] : null;
        const expectedKey = era ? `compliance_${era}.json` : 'compliance.json';

        let file = (userFiles || []).find(f => f.key === expectedKey);
        if (!file && era) {
            // Legacy continuation: protocol migrated to dated before compliance did.
            // Use the plain file only while no dated compliance exists; our next
            // sync writes compliance_<era>.json and completes the migration.
            // (Old-era days are trimmed at upload, so a regenerated era stays clean.)
            const hasDatedCompliance = (userFiles || []).some(f => /^compliance_\d{8}\.json$/.test(f.key));
            if (!hasDatedCompliance) {
                file = (userFiles || []).find(f => f.key === 'compliance.json');
            }
        }
        if (!file) return { serverData: null, eraDate: era };
        try {
            const parsed = typeof file.data === 'string' ? JSON.parse(file.data) : file.data;
            return { serverData: parsed, eraDate: era };
        } catch {
            return { serverData: null, eraDate: era };
        }
    }, [userFiles]);

    return useComplianceState(supplements, { serverData, patientId, userId, weeklySupplements, eraDate });
}
